import { httpsCallable } from 'firebase/functions';
import { profilesStore, Profile } from '../modules/profiles/profiles.store';
import { authStore } from '../modules/auth/auth.store';
import { router } from '../app/router';
import { functions } from '../config/firebase';
import { escapeHTML, truncate } from '../shared/lib/format';
import { showToast } from '../shared/components/Toast';

let activeProfileForModal: Profile | null = null;

export function renderDiscoverPage(container: HTMLElement): void {
  if (!authStore.isAuthenticated) {
    router.navigate('/auth');
    return;
  }

  profilesStore.reset();

  container.innerHTML = `
    <main class="section pt-nav">
      <div class="container">
        <div class="section-header text-center">
          <div class="badge badge-accent mb-2"><i class="fa-solid fa-fire"></i> Live Discovery</div>
          <h2>Browse Profiles</h2>
          <p class="section-subtitle">Find genuine people seeking authentic connections.</p>
        </div>

        <!-- Weekly Spotlight Banner -->
        <div id="weeklySpotlightArea" class="mb-8"></div>

        <!-- Filters -->
        <div class="card card-body mb-8">
          <div class="flex gap-4" style="flex-wrap:wrap">
            <input type="text" id="searchInput" class="form-input" placeholder="Search by name, bio, or city..." style="flex:1;min-width:200px">
            <select id="genderFilter" class="form-select" style="min-width:140px">
              <option value="all">All Genders</option>
              <option value="female">Female</option>
              <option value="male">Male</option>
            </select>
            <select id="tribeFilter" class="form-select" style="min-width:140px">
              <option value="all">All Tribes</option>
              <option value="Yoruba">Yoruba</option>
              <option value="Igbo">Igbo</option>
              <option value="Hausa">Hausa</option>
              <option value="Edo">Edo</option>
              <option value="Ijaw">Ijaw</option>
            </select>
            <select id="intentFilter" class="form-select" style="min-width:160px">
              <option value="all">All Relationship Intent</option>
              <option value="Marriage">Marriage</option>
              <option value="Serious Relationship">Serious Relationship</option>
              <option value="Casual Dating">Casual Dating</option>
              <option value="Friendship">Friendship</option>
            </select>
          </div>
        </div>

        <!-- Profiles Grid -->
        <div id="profilesGrid" class="grid grid-3 gap-8" aria-live="polite"></div>

        <!-- Load More -->
        <div id="loadMoreArea" class="text-center mt-8 hidden">
          <button class="btn btn-secondary" id="loadMoreBtn">Load More Profiles</button>
        </div>
      </div>
      <audio id="globalAudioPlayer" hidden></audio>

      <!-- Profile Detail Modal Overlay -->
      <div class="modal-overlay" id="profileDetailModal">
        <div class="modal-card profile-detail-modal" id="profileDetailCard"></div>
      </div>

      <!-- VIP WhatsApp Modal Overlay -->
      <div class="modal-overlay" id="whatsappModal">
        <div class="modal-card text-center" style="max-width:400px">
          <button class="modal-close" id="whatsappModalClose"><i class="fa-solid fa-xmark"></i></button>
          <div class="text-4xl mb-3" style="color:var(--color-success)"><i class="fa-brands fa-whatsapp"></i></div>
          <h3 class="mb-2">WhatsApp Contact Unlocked</h3>
          <p class="text-sm mb-4" style="color:var(--color-text-muted)">You are a Zuri VIP member! You can contact this member directly on WhatsApp.</p>

          <div class="card card-body mb-6" style="background:var(--color-bg-raised)">
            <div class="text-xs" style="color:var(--color-text-muted)">Phone / WhatsApp Number</div>
            <div class="text-xl font-bold mt-1" id="waModalPhone">Loading…</div>
          </div>

          <div class="flex flex-col gap-3">
            <a href="#" target="_blank" rel="noopener" id="waModalLaunchBtn" class="btn btn-primary w-full">
              <i class="fa-brands fa-whatsapp"></i> Launch WhatsApp Chat
            </a>
            <button class="btn btn-secondary w-full" id="waModalCopyBtn">
              <i class="fa-regular fa-copy"></i> Copy Phone Number
            </button>
          </div>
        </div>
      </div>
    </main>
  `;

  // Subscribe to profiles store and use filteredProfiles
  const unsub = profilesStore.subscribe(() => {
    renderProfileCards(profilesStore.filteredProfiles);
    const loadArea = document.getElementById('loadMoreArea');
    if (loadArea) loadArea.classList.toggle('hidden', !profilesStore.hasMore && !profilesStore.loading);
  });

  // Load profiles initial batch & weekly spotlight
  profilesStore.loadProfiles();
  loadWeeklySpotlight();

  // Wire filter inputs
  document.getElementById('searchInput')?.addEventListener('input', (e) => {
    profilesStore.setFilters({ query: (e.target as HTMLInputElement).value });
  });
  document.getElementById('genderFilter')?.addEventListener('change', (e) => {
    profilesStore.setFilters({ gender: (e.target as HTMLSelectElement).value });
  });
  document.getElementById('tribeFilter')?.addEventListener('change', (e) => {
    profilesStore.setFilters({ tribe: (e.target as HTMLSelectElement).value });
  });
  document.getElementById('intentFilter')?.addEventListener('change', (e) => {
    profilesStore.setFilters({ intent: (e.target as HTMLSelectElement).value });
  });
  document.getElementById('loadMoreBtn')?.addEventListener('click', () => profilesStore.loadProfiles());

  // WhatsApp Modal Close Handler
  document.getElementById('whatsappModalClose')?.addEventListener('click', () => {
    document.getElementById('whatsappModal')?.classList.remove('open');
  });

  // Cleanup on unmount
  (container as unknown as { _cleanup?: () => void })._cleanup = unsub;
}

function renderProfileCards(profiles: Profile[]): void {
  const grid = document.getElementById('profilesGrid');
  if (!grid) return;

  if (profiles.length === 0) {
    grid.innerHTML = `
      <div class="empty-state" style="grid-column:1/-1">
        <i class="fa-solid fa-users-slash"></i>
        <h3>No profiles match your filter</h3>
        <p>Try broadening your search or resetting filters.</p>
      </div>
    `;
    return;
  }

  const isPremium = authStore.isPremium;

  grid.innerHTML = profiles.map(p => {
    const name = escapeHTML(p.displayName || 'Member');
    const age = p.age || '';
    const city = escapeHTML(p.city || '');
    const bio = escapeHTML(p.bio || '');
    const intent = escapeHTML(p.relationshipIntent || '');
    const tribe = escapeHTML(p.tribe || '');
    const religion = escapeHTML(p.religion || '');
    const photo = p.photoUrls?.[0] || '';
    const verified = p.verified;
    const voiceUrl = escapeHTML(p.voiceNoteUrl || '');

    return `
      <article class="profile-card js-profile-card" data-id="${p.id}">
        <div class="profile-card-image" style="cursor:pointer">
          ${photo
            ? `<img src="${escapeHTML(photo)}" alt="${name}" loading="lazy" />`
            : `<div class="fallback"><i class="fa-solid fa-user"></i></div>`
          }
          ${verified ? '<span class="badge badge-verified" style="position:absolute;top:12px;right:12px"><i class="fa-solid fa-circle-check"></i> Verified</span>' : ''}
          ${voiceUrl ? '<span style="position:absolute;bottom:12px;left:12px;background:rgba(0,0,0,0.65);color:#fff;padding:4px 10px;border-radius:999px;font-size:11px"><i class="fa-solid fa-microphone"></i> Voice Intro</span>' : ''}
        </div>
        <div class="profile-card-body">
          <div class="name-age" style="cursor:pointer">${name}${age ? `, ${age}` : ''}</div>
          <div class="location"><i class="fa-solid fa-location-dot"></i> ${city}</div>
          <div class="flex gap-2 mt-2" style="flex-wrap:wrap">
            ${intent ? `<span class="tag"><i class="fa-solid fa-hand-holding-heart"></i> ${intent}</span>` : ''}
            ${tribe ? `<span class="tag"><i class="fa-solid fa-people-group"></i> ${tribe}</span>` : ''}
            ${religion ? `<span class="tag"><i class="fa-solid fa-mosque"></i> ${religion}</span>` : ''}
          </div>
          ${bio ? `<p class="text-sm mt-2">${truncate(escapeHTML(bio), 110)}</p>` : ''}

          ${voiceUrl ? `
            <div class="audio-player mt-2 voice-player-box" data-url="${voiceUrl}">
              <button class="audio-play-btn voice-play-btn" type="button" data-url="${voiceUrl}" data-profile-id="${p.id}">
                <i class="fa-solid fa-play"></i>
              </button>
              <div class="waveform-container">
                <div class="waveform-bar"></div>
                <div class="waveform-bar"></div>
                <div class="waveform-bar"></div>
                <div class="waveform-bar"></div>
                <div class="waveform-bar"></div>
              </div>
              <span class="audio-label">Voice Intro</span>
            </div>
          ` : ''}

          <div class="mt-4">
            ${isPremium
              ? `<button class="btn btn-primary w-full js-wa-contact-btn" data-id="${p.id}"><i class="fa-brands fa-whatsapp"></i> Chat on WhatsApp</button>`
              : `<button class="btn btn-secondary w-full js-upgrade-btn"><i class="fa-solid fa-lock"></i> Unlock WhatsApp Contact</button>`
            }
          </div>
        </div>
      </article>
    `;
  }).join('');

  // Wire profile card click to open detail modal
  grid.querySelectorAll('.js-profile-card').forEach(card => {
    const profileId = (card as HTMLElement).dataset.id;
    const targetProfile = profiles.find(p => p.id === profileId);

    card.querySelectorAll('.profile-card-image, .name-age').forEach(el => {
      el.addEventListener('click', () => {
        if (targetProfile) openProfileDetailModal(targetProfile);
      });
    });
  });

  // Wire upgrade buttons
  grid.querySelectorAll('.js-upgrade-btn').forEach(btn => {
    btn.addEventListener('click', () => router.navigate('/membership'));
  });

  // Wire WhatsApp contact modal for VIPs
  grid.querySelectorAll('.js-wa-contact-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = (btn as HTMLElement).dataset.id;
      const p = profiles.find(item => item.id === id);
      if (p) void openWhatsAppModal(p);
    });
  });

  // Wire voice playback buttons
  wireVoicePlayback(grid);
}

function openProfileDetailModal(p: Profile): void {
  activeProfileForModal = p;
  const modalOverlay = document.getElementById('profileDetailModal');
  const modalCard = document.getElementById('profileDetailCard');
  if (!modalOverlay || !modalCard) return;

  // Fire-and-forget view analytics — a failure here shouldn't block the modal.
  recordImpressionSafe(p.id, 'view');

  const isPremium = authStore.isPremium;
  const photo = p.photoUrls?.[0] || '';
  const name = escapeHTML(p.displayName);
  const voiceUrl = escapeHTML(p.voiceNoteUrl || '');

  modalCard.innerHTML = `
    <button class="modal-close" id="modalDetailClose" style="z-index:10;background:rgba(0,0,0,0.5);color:#fff"><i class="fa-solid fa-xmark"></i></button>
    <div class="profile-detail-hero">
      ${photo
        ? `<img src="${escapeHTML(photo)}" alt="${name}" />`
        : `<div class="fallback" style="display:flex;align-items:center;justify-content:center;height:100%;font-size:4rem;color:var(--color-text-muted)"><i class="fa-solid fa-user"></i></div>`
      }
      ${p.verified ? '<span class="badge badge-verified" style="position:absolute;top:16px;left:16px;z-index:2"><i class="fa-solid fa-circle-check"></i> Photo Verified</span>' : ''}
    </div>
    <div class="profile-detail-body">
      <div class="flex items-center justify-between">
        <h2>${name}, ${escapeHTML(String(p.age))}</h2>
        <span class="tag"><i class="fa-solid fa-location-dot"></i> ${escapeHTML(p.city)}</span>
      </div>

      <div class="flex gap-2 flex-wrap">
        ${p.relationshipIntent ? `<span class="tag"><i class="fa-solid fa-hand-holding-heart"></i> ${escapeHTML(p.relationshipIntent)}</span>` : ''}
        ${p.tribe ? `<span class="tag"><i class="fa-solid fa-people-group"></i> ${escapeHTML(p.tribe)}</span>` : ''}
        ${p.religion ? `<span class="tag"><i class="fa-solid fa-mosque"></i> ${escapeHTML(p.religion)}</span>` : ''}
      </div>

      ${p.bio ? `
        <div>
          <h4 class="text-sm font-bold mb-1" style="color:var(--color-text-muted)">About ${name}</h4>
          <p class="text-sm">${escapeHTML(p.bio)}</p>
        </div>
      ` : ''}

      ${(p.interests && p.interests.length > 0) ? `
        <div>
          <h4 class="text-sm font-bold mb-2" style="color:var(--color-text-muted)">Interests</h4>
          <div class="flex gap-2 flex-wrap">
            ${p.interests.map(i => `<span class="tag">${escapeHTML(i)}</span>`).join('')}
          </div>
        </div>
      ` : ''}

      ${voiceUrl ? `
        <div class="audio-player mt-2 voice-player-box" data-url="${voiceUrl}">
          <button class="audio-play-btn voice-play-btn" type="button" data-url="${voiceUrl}" data-profile-id="${p.id}">
            <i class="fa-solid fa-play"></i>
          </button>
          <div class="waveform-container">
            <div class="waveform-bar"></div>
            <div class="waveform-bar"></div>
            <div class="waveform-bar"></div>
            <div class="waveform-bar"></div>
            <div class="waveform-bar"></div>
          </div>
          <span class="audio-label">Listen to ${name}'s Voice Intro</span>
        </div>
      ` : ''}

      <div class="mt-4">
        ${isPremium
          ? `<button class="btn btn-primary w-full" id="modalDetailWaBtn"><i class="fa-brands fa-whatsapp"></i> Chat on WhatsApp</button>`
          : `<button class="btn btn-secondary w-full" id="modalDetailUpgradeBtn"><i class="fa-solid fa-lock"></i> Unlock WhatsApp Contact</button>`
        }
      </div>
    </div>
  `;

  modalOverlay.classList.add('open');

  modalCard.querySelector('#modalDetailClose')?.addEventListener('click', () => modalOverlay.classList.remove('open'));
  modalCard.querySelector('#modalDetailUpgradeBtn')?.addEventListener('click', () => {
    modalOverlay.classList.remove('open');
    router.navigate('/membership');
  });
  modalCard.querySelector('#modalDetailWaBtn')?.addEventListener('click', () => {
    modalOverlay.classList.remove('open');
    if (activeProfileForModal) void openWhatsAppModal(activeProfileForModal);
  });

  wireVoicePlayback(modalCard);
}

/**
 * Opens the WhatsApp contact modal and fetches the real number from the
 * revealWhatsAppContact callable — the number is never present on the
 * client-side Profile object (publicProfiles intentionally omits it), so
 * this is the only way to actually get it.
 */
async function openWhatsAppModal(p: Profile): Promise<void> {
  const modal = document.getElementById('whatsappModal');
  const phoneEl = document.getElementById('waModalPhone');
  const launchBtn = document.getElementById('waModalLaunchBtn') as HTMLAnchorElement | null;
  const copyBtn = document.getElementById('waModalCopyBtn');

  if (!modal || !phoneEl) return;

  phoneEl.textContent = 'Loading…';
  if (launchBtn) launchBtn.href = '#';
  modal.classList.add('open');

  try {
    const revealWhatsAppContact = httpsCallable<{ profileId: string }, { whatsappNumber: string }>(
      functions,
      'revealWhatsAppContact'
    );
    const result = await revealWhatsAppContact({ profileId: p.id });
    const phone = result.data.whatsappNumber;

    if (!phone) {
      phoneEl.textContent = 'Not provided';
      showToast('This member hasn\u2019t added a WhatsApp number yet.', 'info');
      return;
    }

    const cleanPhone = phone.replace(/[^0-9+]/g, '');
    phoneEl.textContent = phone;

    if (launchBtn) {
      launchBtn.href = `https://wa.me/${cleanPhone.replace('+', '')}?text=${encodeURIComponent(`Hi ${p.displayName}, I connected with your profile on Zuri!`)}`;
    }

    if (copyBtn) {
      copyBtn.onclick = () => {
        navigator.clipboard.writeText(cleanPhone);
        showToast('WhatsApp number copied to clipboard!', 'success');
      };
    }
  } catch (err) {
    console.error('Failed to reveal WhatsApp contact:', err);
    const code = (err as { code?: string })?.code || '';
    modal.classList.remove('open');
    if (code === 'permission-denied' || code === 'functions/permission-denied') {
      showToast('WhatsApp contact is a VIP feature. Upgrade to unlock it.', 'info');
      router.navigate('/membership');
    } else {
      showToast('Could not load WhatsApp contact. Please try again.', 'error');
    }
  }
}

function wireVoicePlayback(parentEl: HTMLElement): void {
  const player = document.getElementById('globalAudioPlayer') as HTMLAudioElement | null;
  if (!player) return;

  parentEl.querySelectorAll('.voice-play-btn').forEach(btn => {
    btn.addEventListener('click', function(this: HTMLButtonElement, e) {
      e.stopPropagation();
      const url = this.dataset.url || '';
      if (!url) return;

      const playerBox = this.closest('.voice-player-box');
      const allBoxes = document.querySelectorAll('.voice-player-box');
      const allBtns = document.querySelectorAll('.voice-play-btn');

      if (!player.paused && player.src.includes(url)) {
        player.pause();
        allBoxes.forEach(b => b.classList.remove('playing'));
        allBtns.forEach(b => b.innerHTML = '<i class="fa-solid fa-play"></i>');
        return;
      }

      allBoxes.forEach(b => b.classList.remove('playing'));
      allBtns.forEach(b => b.innerHTML = '<i class="fa-solid fa-play"></i>');

      const profileId = this.dataset.profileId;
      if (profileId) recordImpressionSafe(profileId, 'voice_play');

      player.src = url;
      player.play().then(() => {
        playerBox?.classList.add('playing');
        this.innerHTML = '<i class="fa-solid fa-pause"></i>';
      }).catch(() => {
        showToast("Couldn't play voice intro.", 'error');
      });

      player.onended = () => {
        playerBox?.classList.remove('playing');
        this.innerHTML = '<i class="fa-solid fa-play"></i>';
      };
      player.onpause = () => {
        playerBox?.classList.remove('playing');
        this.innerHTML = '<i class="fa-solid fa-play"></i>';
      };
    });
  });
}

/**
 * Best-effort analytics call — never blocks or surfaces errors to the user,
 * since a failed impression log shouldn't interrupt browsing.
 */
function recordImpressionSafe(profileId: string, action: 'view' | 'voice_play' | 'whatsapp_click'): void {
  const recordImpression = httpsCallable(functions, 'recordImpression');
  recordImpression({ profileId, action }).catch((err) => {
    console.warn('recordImpression failed (non-blocking):', err);
  });
}

async function loadWeeklySpotlight(): Promise<void> {
  const mount = document.getElementById('weeklySpotlightArea');
  if (!mount) return;

  try {
    const getFeatures = httpsCallable<unknown, { profiles: Profile[] }>(functions, 'getWeeklyFeatures');
    const result = await getFeatures();
    const profiles = result.data?.profiles || [];

    if (profiles.length === 0) return;

    const woman = profiles.find((p) => p.gender === 'female');
    const man = profiles.find((p) => p.gender === 'male');

    mount.innerHTML = `
      <div class="card card-body border-gold" style="background: linear-gradient(135deg, var(--color-bg-surface), var(--color-bg-raised)); position: relative; overflow: hidden;">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-2">
            <i class="fa-solid fa-crown text-gold text-xl"></i>
            <h3 class="font-bold text-lg">Weekly Spotlights</h3>
          </div>
          <span class="badge badge-gold"><i class="fa-solid fa-star"></i> Featured Members</span>
        </div>

        <div class="grid grid-2 gap-4">
          ${woman ? `
            <div class="flex gap-4 items-center p-3 rounded" style="background: var(--color-bg-base);">
              <img src="${escapeHTML(woman.photoUrls?.[0] || 'https://placehold.co/100x100?text=Zuri')}" alt="${escapeHTML(woman.displayName)}" class="avatar avatar-lg" style="object-fit:cover; border:2px solid var(--color-gold);" />
              <div>
                <span class="badge badge-accent mb-1" style="font-size:0.7rem;">Girl of the Week</span>
                <h4 class="font-bold">${escapeHTML(woman.displayName)}, ${woman.age}</h4>
                <p class="text-xs text-muted"><i class="fa-solid fa-location-dot"></i> ${escapeHTML(woman.city || 'Nigeria')}</p>
                <p class="text-xs mt-1" style="color:var(--color-text-muted); line-clamp:2;">${escapeHTML(truncate(woman.bio || '', 60))}</p>
              </div>
            </div>
          ` : ''}

          ${man ? `
            <div class="flex gap-4 items-center p-3 rounded" style="background: var(--color-bg-base);">
              <img src="${escapeHTML(man.photoUrls?.[0] || 'https://placehold.co/100x100?text=Zuri')}" alt="${escapeHTML(man.displayName)}" class="avatar avatar-lg" style="object-fit:cover; border:2px solid var(--color-gold);" />
              <div>
                <span class="badge badge-accent mb-1" style="font-size:0.7rem;">Guy of the Week</span>
                <h4 class="font-bold">${escapeHTML(man.displayName)}, ${man.age}</h4>
                <p class="text-xs text-muted"><i class="fa-solid fa-location-dot"></i> ${escapeHTML(man.city || 'Nigeria')}</p>
                <p class="text-xs mt-1" style="color:var(--color-text-muted); line-clamp:2;">${escapeHTML(truncate(man.bio || '', 60))}</p>
              </div>
            </div>
          ` : ''}
        </div>
      </div>
    `;
  } catch (err) {
    console.warn('Failed to load weekly spotlight features:', err);
  }
}

